import React, { useEffect, useMemo, useState } from 'react';
import { Calendar, Video, MessageSquare, Phone, CheckCircle2, XCircle, AlertTriangle, Play, Clock, Zap } from 'lucide-react';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { useToast } from '../ui/Toast';
import { useLanguage } from '../../i18n/LanguageContext';
import { ZodiacTheme } from './zodiacThemes';
import { VideoCallModal } from '../ui/VideoCallModal';
interface ConsultationsUserTabProps {
  theme: ZodiacTheme;
  onNavigate: (page: string) => void;
  setActiveTab: (tab: string) => void;
}
type ConsultationStatus = 'upcoming' | 'completed' | 'cancelled' | 'rescheduled';
type Format = 'video' | 'chat' | 'audio';
interface Consultation {
  id: string;
  astrologer: string;
  astrologerInitials: string;
  service: string;
  date: string;
  time: string;
  duration: string;
  format: Format;
  status: ConsultationStatus;
  payment: 'paid' | 'pending';
  link?: string;
  minutesUntil?: number;
}
const FORMAT_CONFIG: Record<Format, {
  icon: React.ReactNode;
  label: string;
  color: string;
}> = {
  video: {
    icon: <Video className="w-3.5 h-3.5" data-id="element-19248" />,
    label: 'Видео',
    color: 'text-indigo-600'
  },
  audio: {
    icon: <Phone className="w-3.5 h-3.5" data-id="element-19249" />,
    label: 'Аудио',
    color: 'text-amber-600'
  },
  chat: {
    icon: <MessageSquare className="w-3.5 h-3.5" data-id="element-19250" />,
    label: 'Чат',
    color: 'text-purple-600'
  }
};
export function ConsultationsUserTab({
  theme,
  onNavigate,
  setActiveTab
}: ConsultationsUserTabProps) {
  const {
    showToast
  } = useToast();
  const {
    t
  } = useLanguage();
  const [filter, setFilter] = useState<ConsultationStatus | 'all'>('upcoming');
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [activeAstrologer, setActiveAstrologer] = useState('');
  const [timerTick, setTimerTick] = useState(0);
  const [consultations, setConsultations] = useState<Consultation[]>([{
    id: 'C-1042',
    astrologer: 'Елена Смирнова',
    astrologerInitials: 'ЕС',
    service: 'Натальная карта',
    date: 'Сегодня',
    time: '14:00',
    duration: '60 мин',
    format: 'video',
    status: 'upcoming',
    payment: 'paid',
    link: 'zoom.us/j/123456789',
    minutesUntil: 25
  }, {
    id: 'C-1043',
    astrologer: 'Дмитрий Волков',
    astrologerInitials: 'ДВ',
    service: 'Соляр на год',
    date: 'Завтра',
    time: '16:30',
    duration: '45 мин',
    format: 'audio',
    status: 'upcoming',
    payment: 'pending',
    link: 'meet.google.com/abc',
    minutesUntil: 1590
  }, {
    id: 'C-1040',
    astrologer: 'Ольга Сидорова',
    astrologerInitials: 'ОС',
    service: 'Экспресс-ответ',
    date: '15 Октября',
    time: '09:00',
    duration: '15 мин',
    format: 'chat',
    status: 'completed',
    payment: 'paid'
  }, {
    id: 'C-1039',
    astrologer: 'Анна Ковалева',
    astrologerInitials: 'АК',
    service: 'Синастрия',
    date: '10 Октября',
    time: '19:00',
    duration: '30 мин',
    format: 'video',
    status: 'cancelled',
    payment: 'paid'
  }]);
  useEffect(() => {
    const interval = setInterval(() => setTimerTick(t => t + 1), 60000);
    return () => clearInterval(interval);
  }, []);
  const upcoming = consultations.filter(c => c.status === 'upcoming');
  const nextConsultation = upcoming.length > 0 ? upcoming.reduce((a, b) => (a.minutesUntil || 9999) < (b.minutesUntil || 9999) ? a : b) : null;
  const counts = useMemo(() => ({
    upcoming: consultations.filter(c => c.status === 'upcoming').length,
    completed: consultations.filter(c => c.status === 'completed').length,
    cancelled: consultations.filter(c => c.status === 'cancelled').length,
    all: consultations.length
  }), [consultations]);
  const filtered = consultations.filter(c => {
    if (filter === 'all') return true;
    return c.status === filter;
  });
  const handleStartSession = (c: Consultation) => {
    if (c.format === 'chat') {
      setActiveTab('chat');
    } else {
      setActiveAstrologer(c.astrologer);
      setShowVideoModal(true);
    }
  };
  const handleCancel = (id: string) => {
    setConsultations(prev => prev.map(c => c.id === id ? {
      ...c,
      status: 'cancelled' as const
    } : c));
    showToast('Консультация отменена');
  };
  const isSoon = (c: Consultation) => (c.minutesUntil || 9999) <= 30;
  const isLive = (c: Consultation) => (c.minutesUntil || 9999) <= 0;
  const formatCountdown = (minutes?: number) => {
    if (!minutes || minutes <= 0) return 'Сейчас';
    if (minutes < 60) return `${minutes} мин`;
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return m > 0 ? `${h} ч ${m} мин` : `${h} ч`;
  };
  const getStatusInfo = (c: Consultation) => {
    if (c.status === 'upcoming' && isLive(c)) return {
      label: 'Идёт сейчас',
      variant: 'error' as const
    };
    if (c.status === 'upcoming' && isSoon(c)) return {
      label: 'Скоро',
      variant: 'warning' as const
    };
    if (c.status === 'upcoming') return {
      label: 'Предстоит',
      variant: 'info' as const
    };
    if (c.status === 'completed') return {
      label: 'Завершена',
      variant: 'success' as const
    };
    if (c.status === 'cancelled') return {
      label: 'Отменена',
      variant: 'neutral' as const
    };
    return {
      label: 'Перенесена',
      variant: 'warning' as const
    };
  };
  return <div className="space-y-5" data-id="element-19251">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-id="element-19252">
        <h2 className="text-xl sm:text-2xl font-bold text-slate-900" data-id="element-19253">
          {t('userdash.consultations.title')}
        </h2>
      </div>

      {/* Next Consultation Hero */}
      {nextConsultation && nextConsultation.status === 'upcoming' && <div className="bg-white border border-slate-200 rounded-xl p-4 sm:p-5 shadow-sm" data-id="element-19254">
          <div className="flex items-center gap-2 mb-3 flex-wrap" data-id="element-19255">
            {isLive(nextConsultation) ? <span className="flex items-center gap-1.5 text-xs font-bold bg-rose-50 text-rose-700 px-2.5 py-1 rounded-full" data-id="element-19256">
                <Zap className="w-3 h-3" data-id="element-19257" /> Идёт сейчас
              </span> : <span className="flex items-center gap-1.5 text-xs font-bold bg-indigo-50 text-indigo-700 px-2.5 py-1 rounded-full" data-id="element-19258">
                <Clock className="w-3 h-3" data-id="element-19259" /> Через{' '}
                {formatCountdown(nextConsultation.minutesUntil)}
              </span>}
            <span className={`flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-slate-100 ${FORMAT_CONFIG[nextConsultation.format].color}`} data-id="element-19260">
              {FORMAT_CONFIG[nextConsultation.format].icon}
              <span className="ml-0.5" data-id="element-19261">
                {FORMAT_CONFIG[nextConsultation.format].label}
              </span>
            </span>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4" data-id="element-19262">
            <div data-id="element-19263">
              <div className="text-3xl sm:text-4xl font-bold text-slate-900 mb-1 tracking-tight" data-id="element-19264">
                {nextConsultation.time}
              </div>
              <div className="text-slate-500 text-sm" data-id="element-19265">
                {nextConsultation.date} · {nextConsultation.duration}
              </div>
              <div className="mt-2" data-id="element-19266">
                <div className="font-semibold text-base text-slate-900" data-id="element-19267">
                  {nextConsultation.astrologer}
                </div>
                <div className="text-slate-500 text-sm" data-id="element-19268">
                  {nextConsultation.service}
                </div>
              </div>
            </div>

            <div className="flex gap-2 shrink-0" data-id="element-19269">
              <Button size="sm" className="font-bold text-xs px-4" onClick={() => handleStartSession(nextConsultation)} data-id="element-19270">
                <Play className="w-4 h-4 mr-1.5" data-id="element-19271" />
                {isLive(nextConsultation) ? 'Войти' : 'Начать'}
              </Button>
              <Button variant="outline" size="sm" className="text-xs" onClick={() => setActiveTab('chat')} data-id="element-19272">
                <MessageSquare className="w-4 h-4 mr-1" data-id="element-19273" /> Чат
              </Button>
            </div>
          </div>

          {nextConsultation.payment === 'pending' && <div className="mt-3 flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-800" data-id="element-19274">
              <AlertTriangle className="w-3.5 h-3.5 shrink-0" data-id="element-19275" />
              <span data-id="element-19276">Ожидает оплаты</span>
              <button onClick={() => showToast('Переход к оплате...')} className="ml-auto underline font-medium hover:no-underline" data-id="element-19277">
                Оплатить
              </button>
            </div>}
        </div>}

      {/* Filter Tabs */}
      <div className="flex gap-1.5 overflow-x-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none] pb-0.5" data-id="element-19278">
        {([{
        id: 'upcoming',
        label: t('userdash.consultations.upcoming')
      }, {
        id: 'completed',
        label: t('userdash.consultations.completed')
      }, {
        id: 'cancelled',
        label: t('userdash.consultations.cancelled')
      }, {
        id: 'all',
        label: t('userdash.consultations.all') || 'Все'
      }] as const).map(tab => <button key={tab.id} onClick={() => setFilter(tab.id)} className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors shrink-0 ${filter === tab.id ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`} data-id="element-19279">
            {tab.label} ({counts[tab.id]})
          </button>)}
      </div>

      {/* Consultations List */}
      {filtered.length > 0 ? <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm divide-y divide-slate-100" data-id="element-19280">
          {filtered.map(c => {
        const statusInfo = getStatusInfo(c);
        const fmt = FORMAT_CONFIG[c.format];
        const soon = isSoon(c);
        const live = isLive(c);
        return <div key={c.id} className={`${c.status === 'upcoming' ? '' : 'opacity-70'}`} data-id="element-19281">
                <div className="flex items-center gap-3 px-4 py-3" data-id="element-19282">
                  {/* Time block */}
                  <div className={`w-14 h-14 rounded-xl flex flex-col items-center justify-center shrink-0 ${live ? 'bg-rose-50 border border-rose-200' : soon ? 'bg-amber-50 border border-amber-200' : 'bg-slate-50 border border-slate-100'}`} data-id="element-19283">
                    <span className={`text-[10px] font-medium ${live ? 'text-rose-500' : soon ? 'text-amber-500' : 'text-slate-500'}`} data-id="element-19284">
                      {c.date === 'Сегодня' ? 'СЕГ' : c.date === 'Завтра' ? 'ЗАВ' : c.date.split(' ')[0]}
                    </span>
                    <span className={`text-base font-bold leading-none ${live ? 'text-rose-700' : soon ? 'text-amber-700' : 'text-slate-900'}`} data-id="element-19285">
                      {c.time}
                    </span>
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0" data-id="element-19286">
                    <div className="flex items-center gap-2 mb-0.5 flex-wrap" data-id="element-19287">
                      <span className="font-semibold text-sm text-slate-900 truncate" data-id="element-19288">
                        {c.astrologer}
                      </span>
                      {c.payment === 'paid' && c.status === 'upcoming' && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0" data-id="element-19289" />}
                      {c.payment === 'pending' && <span className="text-[9px] font-bold text-rose-600 bg-rose-50 px-1.5 py-0.5 rounded border border-rose-100" data-id="element-19290">
                          Не оплачено
                        </span>}
                    </div>
                    <div className="flex items-center gap-2 text-xs text-slate-500 flex-wrap" data-id="element-19291">
                      <span className="truncate" data-id="element-19292">{c.service}</span>
                      <span className="text-slate-300" data-id="element-19293">·</span>
                      <span data-id="element-19294">{c.duration}</span>
                      <span className="text-slate-300" data-id="element-19295">·</span>
                      <span className={`flex items-center gap-0.5 ${fmt.color}`} data-id="element-19296">
                        {fmt.icon} {fmt.label}
                      </span>
                    </div>
                    {c.status === 'upcoming' && c.minutesUntil !== undefined && c.minutesUntil > 0 && <div className="flex items-center gap-1 mt-1 text-[11px] text-slate-400" data-id="element-19297">
                          <Clock className="w-3 h-3" data-id="element-19298" /> Через{' '}
                          {formatCountdown(c.minutesUntil)}
                        </div>}
                  </div>

                  {/* Status + Actions */}
                  <div className="flex items-center gap-2 shrink-0" data-id="element-19299">
                    {c.status === 'upcoming' ? <>
                        <Badge variant={statusInfo.variant} size="sm" className="text-[9px] py-0 h-5 px-2 hidden sm:flex" data-id="element-19300">
                          {statusInfo.label}
                        </Badge>
                        <Button variant="primary" size="sm" className="text-xs h-8 px-3" onClick={() => handleStartSession(c)} data-id="element-19301">
                          <Play className="w-3.5 h-3.5 mr-1" data-id="element-19302" />
                          {live ? 'Войти' : 'Начать'}
                        </Button>
                        <button onClick={() => setActiveTab('chat')} className="p-2 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors hidden sm:block" title="Написать" data-id="element-19303">
                          <MessageSquare className="w-4 h-4" data-id="element-19304" />
                        </button>
                      </> : <Badge variant={statusInfo.variant} size="sm" className="text-[10px]" data-id="element-19305">
                        {statusInfo.label}
                      </Badge>}
                  </div>
                </div>

                {/* Mobile actions */}
                {c.status === 'upcoming' && <div className="flex items-center gap-2 px-4 pb-3 sm:hidden" data-id="element-19306">
                    <Button variant="primary" size="sm" className="flex-1 text-xs h-8" onClick={() => handleStartSession(c)} data-id="element-19307">
                      <Play className="w-3.5 h-3.5 mr-1" data-id="element-19308" />{' '}
                      {live ? 'Войти' : 'Начать'}
                    </Button>
                    <Button variant="outline" size="sm" className="flex-1 text-xs h-8" onClick={() => setActiveTab('chat')} data-id="element-19309">
                      <MessageSquare className="w-3.5 h-3.5 mr-1" data-id="element-19310" /> Чат
                    </Button>
                    <button onClick={() => handleCancel(c.id)} className="p-2 text-slate-400 hover:text-rose-500 rounded-lg transition-colors" title="Отменить" data-id="element-19311">
                      <XCircle className="w-4 h-4" data-id="element-19312" />
                    </button>
                  </div>}
              </div>;
      })}
        </div> : <div className="text-center py-12" data-id="element-19313">
          <div className="w-14 h-14 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-3" data-id="element-19314">
            <Calendar className="w-7 h-7 text-slate-300" data-id="element-19315" />
          </div>
          <h3 className="font-bold text-slate-900 mb-1 text-sm" data-id="element-19316">
            {t('userdash.consultations.noConsultations')}
          </h3>
          <p className="text-xs text-slate-500 mb-4 max-w-xs mx-auto" data-id="element-19317">
            Выберите астролога и запишитесь на сессию
          </p>
          <Button size="sm" onClick={() => onNavigate('astrologers')} data-id="element-19318">
            {t('userdash.consultations.bookNew')}
          </Button>
        </div>}

      {showVideoModal && <VideoCallModal isOpen={showVideoModal} onClose={() => setShowVideoModal(false)} astrologerName={activeAstrologer} clientName="Вы" mode="video" data-id="element-19319" />}
    </div>;
}